import { useEffect, useState } from 'react';
import { MapPin, Globe, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n, LOCALES } from '../i18n';

const SEEN_KEY = 'gd_loc_onboard_v1';
const LANG_FLAG = 'gd_lang_suggested_v1'; // shared with LanguageWelcome to avoid double prompts

// ISO country → preferred app locale (only suggest when we support the language)
const COUNTRY_TO_LOCALE = {
    dk: 'da',
    de: 'de', at: 'de', ch: 'de', li: 'de',
    se: 'sv',
    no: 'no',
    es: 'es',
    fr: 'fr', mc: 'fr',
    it: 'it', sm: 'it', va: 'it',
    pt: 'pt',
    nl: 'nl', be: 'nl',
    pl: 'pl',
    // explicit anglophone fallbacks → English (don't bother suggesting if already EN)
    gb: 'en', us: 'en', ca: 'en', au: 'en', nz: 'en', ie: 'en',
};

function suggestLocaleSwitch(countryCode, currentLocale, t, setLocale) {
    // Respect a language choice already addressed by the browser-based nudge.
    if (localStorage.getItem(LANG_FLAG) === '1') return;
    const cc = (countryCode || '').toLowerCase();
    const target = COUNTRY_TO_LOCALE[cc];
    if (!target || target === (currentLocale || '').split('-')[0]) return;
    const meta = LOCALES.find((l) => l.code === target);
    if (!meta) return;
    localStorage.setItem(LANG_FLAG, '1');
    const msg = t('location.lang_suggest').replace('{lang}', `${meta.flag} ${meta.label}`);
    toast(msg, {
        duration: 8000,
        action: {
            label: t('location.lang_switch'),
            onClick: () => setLocale(target),
        },
        cancel: { label: t('location.lang_keep'), onClick: () => {} },
    });
}

export default function LocationOnboardModal() {
    const { user, refresh } = useAuth();
    const { t, locale, setLocale } = useI18n();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (user.location?.city) return;                       // already has location
        if (localStorage.getItem(SEEN_KEY) === '1') return;    // user already chose
        const tm = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(tm);
    }, [user]);

    const close = () => { localStorage.setItem(SEEN_KEY, '1'); setOpen(false); };

    const allow = () => {
        if (!navigator.geolocation) { close(); return; }
        setBusy(true);
        navigator.geolocation.getCurrentPosition(async (pos) => {
            const la = pos.coords.latitude, lo = pos.coords.longitude;
            try {
                const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${la}&lon=${lo}&format=json&accept-language=${locale}`);
                const j = await r.json();
                const c = j?.address?.city || j?.address?.town || j?.address?.village || j?.address?.county || 'My location';
                const cc = j?.address?.country_code || '';
                await api.patch('/auth/me', { location: { lat: la, lon: lo, city: c, postcode: j?.address?.postcode, country: cc } });
                refresh();
                // Confirm that weather + the sowing calendar are now region-tailored
                toast.success(t('location.region_set').replace('{city}', c));
                // Suggest language switch when country implies a different supported locale
                suggestLocaleSwitch(cc, locale, t, setLocale);
            } catch {}
            setBusy(false);
            close();
        }, () => { setBusy(false); close(); }, { timeout: 10000, maximumAge: 600000 });
    };

    if (!open) return null;

    return (
        <div data-testid="location-onboard" className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-brand-card border border-brand-border rounded-3xl p-6 max-w-md w-full shadow-2xl">
                <div className="flex items-start justify-between mb-3">
                    <div className="bg-brand-green/10 p-3 rounded-2xl">
                        <Globe size={22} className="text-brand-green" />
                    </div>
                    <button onClick={close} aria-label="Close" className="p-1.5 hover:bg-brand-bg2 rounded-full">
                        <X size={16} />
                    </button>
                </div>
                <h3 className="font-serif text-2xl text-brand-ink mb-2">{t('location.onboard_title')}</h3>
                <p className="text-sm text-brand-ink2 leading-relaxed mb-5">
                    {t('location.onboard_desc')}
                </p>
                <button
                    data-testid="btn-allow-location"
                    onClick={allow}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full text-sm font-medium mb-2 disabled:opacity-60 transition-colors"
                >
                    <MapPin size={15} /> {busy ? '…' : t('location.onboard_allow')}
                </button>
                <button
                    data-testid="btn-skip-location"
                    onClick={close}
                    className="w-full text-brand-ink3 hover:text-brand-ink2 text-sm py-2"
                >
                    {t('location.onboard_skip')}
                </button>
            </div>
        </div>
    );
}
